import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography,
  type Theme,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { useDashboard } from "../hooks/query/useDashboard";
import { ColumnDef, DataTable } from "../components/Table";
import type { BusinessService, ServiceMap } from "../types";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import RowActionsMenu from "../components/RowActionsMenu";
import DialogContext from "../contexts/DialogContext";
import { useServiceMap } from "../hooks/query/useServiceMap";
import { COLORS } from "../theme/theme-options";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ShieldIcon from "@mui/icons-material/Shield";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { alpha } from "@mui/material/styles";
import { MetricCard } from "../components/services/MetricCard";
import { HealthScore } from "../components/services/HealthScore";
import { TrafficLight } from "../components/services/TrafficLight";
import { STATUS_COLOR } from "../constants";
import { DeleteButton } from "../components/buttons/DeleteButton";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { ServiceMapDiagram } from "../components/ServiceMapDiagram";
import { useUpdateServiceMap } from "../hooks/mutation/update/useUpdateServiceMap";

const scoreFallback = (s: BusinessService) =>
  s.status === "healthy" ? 100 : s.status === "warning" ? 60 : 35;

const slaLabel = (s: BusinessService): { label: string; color: string } => {
  const current = s.metrics?.slo;
  const target = s.sla;

  if (typeof current !== "number") {
    return { label: `${target}%`, color: alpha(COLORS.grey[300], 0.75) };
  }
  if (current >= target) {
    return { label: "Within SLA", color: STATUS_COLOR.healthy.main };
  }
  if (current >= target - 2) {
    return { label: "SLA At Risk", color: STATUS_COLOR.warning.main };
  }
  return { label: "SLA Breach", color: STATUS_COLOR.critical.main };
};

const RiskForecastCell: React.FC<{ value?: number }> = ({ value }) => {
  if(!value) {
   return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Typography variant="body2" sx={{ color: COLORS.grey[400], fontWeight: 600 }}>
        Unknown
      </Typography>
    </Box>
  ); 
  }
  const risk = Math.max(0, Math.min(100, value));
  const isHigh = risk >= 60;
  const isMedium = risk >= 30 && risk < 60;
  const Icon = isHigh || isMedium ? TrendingUpIcon : TrendingDownIcon;
  const color = isHigh
    ? STATUS_COLOR.critical.main
    : isMedium
      ? STATUS_COLOR.warning.main
      : STATUS_COLOR.healthy.main;

  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Icon sx={{ fontSize: 16, color }} />
      <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
        {`${Math.round(risk)}%`}
      </Typography>
    </Box>
  );
};

const columns: ColumnDef<BusinessService>[] = [
  {
    id: "service",
    header: "Service",
    width: "30%",
    render: (row) => {
      return row.label;
    },
  },
  {
    id: "health",
    header: "Health",
    align: "center",
    headerAlign: "center",
    render: (row) => {
      const score = row.metrics?.score ?? scoreFallback(row);
      return <HealthScore score={score} size="sm" />;
    }
  },
  {
    id: "status",
    header: "Status",
    align: "center",
    headerAlign: "center",
    render: (row) => {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <TrafficLight status={row.status} size="sm" />
        </Box>
      );
    }
  },
  {
    id: "sla",
    header: "SLA",
    align: "center",
    headerAlign: "center",
    render: (row) => {
      const s = slaLabel(row);
      return (
        <Typography variant="body2" sx={{ color: s.color, fontWeight: 600 }}>
          {s.label}
        </Typography>
      );
    }
  },
  {
    id: "owner",
    header: "Owner",
    render: (row) => {
      return row.owner;
    },
  },
  {
    id: "risk_forecast",
    header: "Risk Forecast",
    render: (row) => {
      const risk = row.metrics?.riskForecast;
      return <RiskForecastCell value={risk} />;
    },
  }
];

const normalizeServiceMapForSave = (serviceMap: ServiceMap): ServiceMap => {
  const nodes = serviceMap.nodes.map((node) => ({
    id: node.id,
    type: node.type,
    position: {
      x: Number(node.position.x.toFixed(2)),
      y: Number(node.position.y.toFixed(2)),
    },
    data: node.data,
  }));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const uniqueEdgesById = new Map<string, ServiceMap["edges"][number]>();

  for (const edge of serviceMap.edges) {
    if (
      !edge.source ||
      !edge.target ||
      edge.source === edge.target ||
      !nodeIds.has(edge.source) ||
      !nodeIds.has(edge.target)
    ) {
      continue;
    }
    const id = `${edge.source}#${edge.target}`;
    uniqueEdgesById.set(id, {
      id,
      source: edge.source,
      target: edge.target,
      type: edge.type,
    });
  }

  return {
    nodes,
    edges: Array.from(uniqueEdgesById.values()),
  };
};

const serializeServiceMap = (serviceMap: ServiceMap): string => {
  const normalized = normalizeServiceMapForSave(serviceMap);
  const nodes = [...normalized.nodes]
    .map((node) => ({
      id: node.id,
      x: node.position.x,
      y: node.position.y,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  const edges = [...normalized.edges]
    .map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify({ nodes, edges });
};

type DiagramEdgeChange = {
  id: string;
  sourceLabel: string;
  targetLabel: string;
};

type DiagramChangesSummary = {
  removedNodes: Array<{ id: string; label: string }>;
  addedEdges: DiagramEdgeChange[];
  removedEdges: DiagramEdgeChange[];
};

const buildDiagramChangesSummary = (
  before: ServiceMap,
  after: ServiceMap,
): DiagramChangesSummary => {
  const beforeNodesById = new Map(
    before.nodes.map((node) => [
      node.id,
      { label: node.data.label, position: node.position },
    ]),
  );
  const afterNodesById = new Map(
    after.nodes.map((node) => [
      node.id,
      { label: node.data.label, position: node.position },
    ]),
  );

  const beforeIds = new Set(before.nodes.map((node) => node.id));
  const afterIds = new Set(after.nodes.map((node) => node.id));

  const removedNodes = Array.from(beforeIds)
    .filter((id) => !afterIds.has(id))
    .map((id) => ({
      id,
      label: beforeNodesById.get(id)?.label ?? id,
    }));

  const toEdgeMap = (serviceMap: ServiceMap) => {
    return new Map(
      serviceMap.edges.map((edge) => [
        edge.id,
        {
          id: edge.id,
          source: edge.source,
          target: edge.target,
        },
      ]),
    );
  };

  const beforeEdgesById = toEdgeMap(before);
  const afterEdgesById = toEdgeMap(after);
  const beforeEdgeIds = new Set(beforeEdgesById.keys());
  const afterEdgeIds = new Set(afterEdgesById.keys());

  const toEdgeChange = (
    edge:
      | {
          id: string;
          source: string;
          target: string;
        }
      | undefined,
  ): DiagramEdgeChange => {
    if (!edge) {
      return {
        id: "",
        sourceLabel: "Unknown",
        targetLabel: "Unknown",
      };
    }
    return {
      id: edge.id,
      sourceLabel:
        afterNodesById.get(edge.source)?.label ??
        beforeNodesById.get(edge.source)?.label ??
        edge.source,
      targetLabel:
        afterNodesById.get(edge.target)?.label ??
        beforeNodesById.get(edge.target)?.label ??
        edge.target,
    };
  };

  const removedEdges = Array.from(beforeEdgeIds)
    .filter((id) => !afterEdgeIds.has(id))
    .map((id) => toEdgeChange(beforeEdgesById.get(id)));
  const addedEdges = Array.from(afterEdgeIds)
    .filter((id) => !beforeEdgeIds.has(id))
    .map((id) => toEdgeChange(afterEdgesById.get(id)));

  return {
    removedNodes,
    addedEdges,
    removedEdges,
  };
};

const hasDiagramStructuralChanges = (summary: DiagramChangesSummary): boolean => {
  return (
    summary.addedEdges.length > 0 ||
    summary.removedNodes.length > 0 ||
    summary.removedEdges.length > 0
  );
};

export const ServicesPage: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: dashboard, isError, isLoading: isDashboardLoading } = useDashboard(dashboardId);
  const { data: serviceMap = { nodes: [], edges: [] }, isLoading: isServiceMapLoading } = useServiceMap(
    dashboard?.id,
  );
  const services = serviceMap.nodes;
  const navigate = useNavigate();
  const { show } = React.useContext(DialogContext);
  const { mutateAsync: updateServiceMap, isPending: isSavingDiagram } =
    useUpdateServiceMap();
  const selectedDashboardId = dashboard?.id ?? dashboardId ?? "";
  const dashboardLabel = dashboard?.name ?? dashboardId ?? "Unknown dashboard";
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const defaultView = isSmallScreen ? "table" : "diagram";
  const requestedView = searchParams.get("view");
  const viewMode: "table" | "diagram" =
    requestedView === "table" || requestedView === "diagram"
      ? requestedView
      : defaultView;
  const [diagramDraft, setDiagramDraft] = React.useState<ServiceMap | null>(null);
  const [isDiagramDirty, setIsDiagramDirty] = React.useState(false);
  const [savePreview, setSavePreview] = React.useState<{
    draft: ServiceMap;
    summary: DiagramChangesSummary;
  } | null>(null);
  const [nodeActionPopover, setNodeActionPopover] = React.useState<{
    nodeId: string;
    left: number;
    top: number;
  } | null>(null);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    service: BusinessService;
  } | null>(null);

  const setServicesViewMode = (mode: "table" | "diagram") => {
    const nextQuery = new URLSearchParams(searchParams);
    if (mode === defaultView) {
      nextQuery.delete("view");
    } else {
      nextQuery.set("view", mode);
    }
    setSearchParams(nextQuery, { replace: true });
    if (mode === "diagram") {
      setSelectedIds([]);
    }
    setNodeActionPopover(null);
  };

  React.useEffect(() => {
    if (isError) {
      navigate("/dashboards", { replace: true });
    }
  }, [isError, navigate]);

  React.useEffect(() => {
    setDiagramDraft(null);
    setIsDiagramDirty(false);
    setSavePreview(null);
  }, [dashboard?.id]);

  React.useEffect(() => {
    if (viewMode !== "diagram") {
      setNodeActionPopover(null);
    }
  }, [viewMode]);

  const openEditServiceDialog = React.useCallback((serviceId: string) => {
    if (!selectedDashboardId || !serviceId) {
      return;
    }
    const nextQuery = new URLSearchParams(searchParams);
    nextQuery.set("dialog", "edit-service");
    nextQuery.set("serviceId", serviceId);
    navigate(`/dashboards/${selectedDashboardId}/services?${nextQuery.toString()}`);
  }, [navigate, searchParams, selectedDashboardId]);

  const openServiceDetails = React.useCallback((serviceId: string) => {
    if (!selectedDashboardId || !serviceId) {
      return;
    }
    navigate(`/dashboards/${selectedDashboardId}/services/${serviceId}`);
  }, [navigate, selectedDashboardId]);

  const openManageDependencies = React.useCallback((serviceId: string) => {
    const serviceMapId = dashboard?.id;
    if (!serviceId || !serviceMapId) {
      return;
    }
    show({
      dialog: "manage-dependencies",
      serviceMapId,
      serviceId,
    });
  }, [dashboard?.id, show]);

  const openManageKpiMapping = React.useCallback((serviceId: string) => {
    const serviceMapId = dashboard?.id;
    if (!serviceId || !serviceMapId) {
      return;
    }
    show({
      dialog: "manage-kpi-alert-mappings",
      serviceMapId,
      serviceId,
    });
  }, [dashboard?.id, show]);

  const baseDiagramSnapshot = React.useMemo(
    () => serializeServiceMap(serviceMap),
    [serviceMap],
  );
  const diagramDraftSnapshot = React.useMemo(
    () => (diagramDraft ? serializeServiceMap(diagramDraft) : ""),
    [diagramDraft],
  );

  React.useEffect(() => {
    if (viewMode !== "diagram" || isDiagramDirty) {
      return;
    }
    const normalizedServiceMap = normalizeServiceMapForSave(serviceMap);
    const nextSnapshot = serializeServiceMap(normalizedServiceMap);
    if (diagramDraftSnapshot === nextSnapshot) {
      return;
    }
    setDiagramDraft(normalizedServiceMap);
    setIsDiagramDirty(false);
  }, [
    dashboard?.id,
    diagramDraftSnapshot,
    isDiagramDirty,
    serviceMap,
    viewMode,
  ]);

  const handleDiagramMapChange = React.useCallback(
    (nextMap: ServiceMap) => {
      const normalizedDraft = normalizeServiceMapForSave(nextMap);
      const nextSnapshot = serializeServiceMap(normalizedDraft);
      setDiagramDraft((currentDraft) => {
        if (!currentDraft) {
          return normalizedDraft;
        }
        const currentSnapshot = serializeServiceMap(currentDraft);
        return currentSnapshot === nextSnapshot ? currentDraft : normalizedDraft;
      });
      setIsDiagramDirty(nextSnapshot !== baseDiagramSnapshot);
    },
    [baseDiagramSnapshot],
  );

  const persistDiagramDraft = React.useCallback(
    async (nextDraft: ServiceMap) => {
      const serviceMapId = dashboard?.id;
      if (!serviceMapId) {
        return;
      }

      await updateServiceMap({
        serviceMapId,
        serviceMap: nextDraft,
      });
      setDiagramDraft(nextDraft);
      setIsDiagramDirty(false);
    },
    [dashboard?.id, updateServiceMap],
  );

  const handleOpenSavePreview = React.useCallback(async () => {
    if (!diagramDraft) {
      return;
    }

    const currentMap = normalizeServiceMapForSave(serviceMap);
    const normalizedDraft = normalizeServiceMapForSave(diagramDraft);
    if (serializeServiceMap(currentMap) === serializeServiceMap(normalizedDraft)) {
      return;
    }
    const summary = buildDiagramChangesSummary(currentMap, normalizedDraft);

    if (!hasDiagramStructuralChanges(summary)) {
      await persistDiagramDraft(normalizedDraft);
      return;
    }

    setSavePreview({
      draft: normalizedDraft,
      summary,
    });
  }, [diagramDraft, persistDiagramDraft, serviceMap]);

  const handleConfirmSaveDiagram = React.useCallback(async () => {
    if (!savePreview) {
      return;
    }
    await persistDiagramDraft(savePreview.draft);
    setSavePreview(null);
  }, [persistDiagramDraft, savePreview]);

  return (
    <Box display="flex" 
        padding={2}
    flexDirection="column" gap={3}>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap={3}
      >
        <Box display="flex" justifyContent="flex-start">
          <Button
            startIcon={<ArrowBackRoundedIcon fontSize="small" />}
            variant="text"
            onClick={() => {
              navigate("/dashboards");
            }}
          >
            Back
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="column">
            <Typography variant="h2" fontWeight={600}>
              Business Services
            </Typography>
            <Box display="flex" alignItems="center" gap={0.8}>
              <DashboardRoundedIcon sx={{ fontSize: 16, color: COLORS.primary[300] }} />
              <Typography variant="body1" color={COLORS.grey[400]}>
                Dashboard:{" "}{dashboardLabel}
              </Typography>
            </Box>
            <Typography variant="body2" color={COLORS.grey[500]}>
              {services.length} services monitored
            </Typography>
          </Box>
          <Box display="flex" justifyContent="end" gap={1.25} alignItems="center">
            <Tooltip title="Table view">
              <IconButton
                aria-label="Table view"
                onClick={() => {
                  setServicesViewMode("table");
                }}
                sx={{
                  borderRadius: 1.6,
                  border: `1px solid ${
                    viewMode === "table"
                      ? alpha(COLORS.primary[300], 0.65)
                      : alpha(COLORS.grey[600], 0.5)
                  }`,
                  color:
                    viewMode === "table"
                      ? COLORS.primary[300]
                      : COLORS.grey[400],
                  backgroundColor:
                    viewMode === "table"
                      ? alpha(COLORS.primary[400], 0.15)
                      : "transparent",
                }}
              >
                <TableRowsRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isSmallScreen && (
              <Tooltip title="Diagram view">
                <IconButton
                  aria-label="Diagram view"
                  onClick={() => {
                    setServicesViewMode("diagram");
                  }}
                  sx={{
                    borderRadius: 1.6,
                    border: `1px solid ${
                      viewMode === "diagram"
                        ? alpha(COLORS.primary[300], 0.65)
                        : alpha(COLORS.grey[600], 0.5)
                    }`,
                    color:
                      viewMode === "diagram"
                        ? COLORS.primary[300]
                        : COLORS.grey[400],
                    backgroundColor:
                      viewMode === "diagram"
                        ? alpha(COLORS.primary[400], 0.15)
                        : "transparent",
                  }}
                >
                  <AccountTreeRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Button
              startIcon={<AddIcon fontSize="small" />}
              href={`/dashboards/${selectedDashboardId}/services?dialog=new-business-service`}
              variant="outlined"
              color="primary"
              disabled={!selectedDashboardId}
            >
              New
            </Button>
            <DeleteButton
              disabled={selectedIds.length === 0}
              onClick={() => {
                if (selectedIds.length === 0) {
                  return;
                }
                show({
                  dialog: "confirm-delete",
                  entity: "service",
                  ids: selectedIds.join(","),
                  serviceMapId: dashboard?.id,
                });
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          <MetricCard
            label="Platform Health"
            value={dashboard?.metrics.platformHealth ?? 0}
            suffix="/100"
            icon={<ShieldIcon />}
            tone="brand"
          />
          <MetricCard
            label="Active Events"
            value={dashboard?.metrics.activeEvents ?? 0}
            suffix=""
            note={`${services.filter((s) => s.data.status !== "healthy").length} services impacted`}
            icon={<WarningAmberIcon />}
            tone="danger"
          />
          <MetricCard
            label="Risk Trending"
            value={dashboard?.metrics.riskTrending ?? 0}
            suffix=""
            note="services at elevated risk"
            icon={<TrendingUpIcon />}
            tone="warning"
          />
        </Box>

        {viewMode === "table" ? (
          <DataTable<BusinessService>
            createItemDialog={
              selectedDashboardId
                ? `/dashboards/${selectedDashboardId}/services?dialog=new-business-service`
                : undefined
            }
            title="All services"
            loading={isDashboardLoading || isServiceMapLoading}
            colSpan={columns.length + 2}
            onRowClick={(r) => {
              navigate(`/dashboards/${selectedDashboardId}/services/${r.id}`);
            }}
            onRowMenuClick={(service, anchorEl) => {
              setRowMenu({
                service,
                anchorEl,
              });
            }}
            onSelectionChange={setSelectedIds}
            rows={services.map((s) => s.data)}
            columns={columns}
          />
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(COLORS.primary[300], 0.2)}`,
              background: `linear-gradient(180deg, ${alpha(
                COLORS.primary[700],
                0.1,
              )} 0%, ${alpha(COLORS.grey[900], 0.65)} 100%)`,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Button
              startIcon={<SaveRoundedIcon fontSize="small" />}
              variant="outlined"
              size="small"
              sx={{
                position: "absolute",
                right: 10,
                top: 8,
                zIndex: 2,
                minWidth: 110,
              }}
              disabled={
                !dashboard?.id ||
                !diagramDraft ||
                !isDiagramDirty ||
                isSavingDiagram
              }
              onClick={() => {
                void handleOpenSavePreview();
              }}
            >
              {isSavingDiagram ? "Saving..." : "Save"}
            </Button>
            <Box sx={{ p: 0.5 }}>
              {serviceMap.nodes.length > 0 ? (
                <ServiceMapDiagram
                  nodes={serviceMap.nodes}
                  edges={serviceMap.edges}
                  variant="full"
                  mode="editor"
                  height="500px"
                  onMapChange={handleDiagramMapChange}
                  onSelectionChange={setSelectedIds}
                  onNodeClick={(nodeId, clickPosition) => {
                    if (!clickPosition) {
                      return;
                    }
                    setNodeActionPopover({
                      nodeId,
                      left: clickPosition.left,
                      top: clickPosition.top,
                    });
                  }}
                />
              ) : (
                <Box
                  sx={{
                    minHeight: 500,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: COLORS.grey[500] }}>
                    No services in this dashboard yet.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <RowActionsMenu
          anchorEl={rowMenu?.anchorEl ?? null}
          open={!!rowMenu}
          onClose={() => setRowMenu(null)}
          onEdit={() => {
            const serviceId = rowMenu?.service.id;
            if (!serviceId) {return;}
            openEditServiceDialog(serviceId);
          }}
          extraActions={[
            {
              key: "explore",
              label: "Explore",
              onClick: () => {
                const serviceId = rowMenu?.service.id;
                if (!serviceId) {return;}
                openServiceDetails(serviceId);
              },
            },
            {
              key: "manage-dependencies",
              label: "Manage dependencies",
              onClick: () => {
                const serviceId = rowMenu?.service.id;
                if (!serviceId) {return;}
                openManageDependencies(serviceId);
              },
            },
            {
              key: "manage-kpi-mapping",
              label: "Manage KPI mapping",
              onClick: () => {
                const serviceId = rowMenu?.service.id;
                if (!serviceId) {return;}
                openManageKpiMapping(serviceId);
              },
            },
          ]}
        />

        <Popover
          open={Boolean(nodeActionPopover)}
          onClose={() => setNodeActionPopover(null)}
          marginThreshold={0}
          PaperProps={{
            sx: {
              p: 0,
              m: 0,
              overflow: "hidden",
            },
          }}
          anchorReference="anchorPosition"
          anchorPosition={
            nodeActionPopover
              ? {
                  top: Math.round(nodeActionPopover.top + 6),
                  left: Math.round(nodeActionPopover.left + 6),
                }
              : undefined
          }
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuList
            dense
            disablePadding
            sx={{
              minWidth: 190,
              py: 0,
              "& .MuiMenuItem-root": {
                px: 1.5,
                py: 1,
                m: 0,
                borderRadius: 0,
                alignItems: "flex-start",
                columnGap: 1,
              },
              "& .MuiMenuItem-root:hover": {
                backgroundColor: alpha(COLORS.primary[500], 0.2),
              },
              "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                {
                  backgroundColor: alpha(COLORS.primary[500], 0.24),
                },
            }}
          >
            <MenuItem
              onClick={() => {
                setNodeActionPopover(null);
              }}
            >
              <ListItemIcon>
                <CheckCircleOutlineRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Select"
                secondary="Use React Flow selection"
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (!nodeActionPopover) {
                  return;
                }
                const selectedNodeId = nodeActionPopover.nodeId;
                setNodeActionPopover(null);
                openEditServiceDialog(selectedNodeId);
              }}
            >
              <ListItemIcon>
                <EditRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Edit" secondary="Open service editor" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (!nodeActionPopover) {
                  return;
                }
                const selectedNodeId = nodeActionPopover.nodeId;
                setNodeActionPopover(null);
                openServiceDetails(selectedNodeId);
              }}
            >
              <ListItemIcon>
                <OpenInNewRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Explore" secondary="Open service details" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                const selectedNodeId = nodeActionPopover?.nodeId;
                setNodeActionPopover(null);
                if (!selectedNodeId) {
                  return;
                }
                openManageDependencies(selectedNodeId);
              }}
            >
              <ListItemIcon>
                <AccountTreeRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Manage dependencies"
                secondary="Edit upstream/downstream links"
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                const selectedNodeId = nodeActionPopover?.nodeId;
                setNodeActionPopover(null);
                if (!selectedNodeId) {
                  return;
                }
                openManageKpiMapping(selectedNodeId);
              }}
            >
              <ListItemIcon>
                <HubRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Manage KPI mapping"
                secondary="Edit KPI to alerts links"
              />
            </MenuItem>
          </MenuList>
        </Popover>

        <Dialog
          open={Boolean(savePreview)}
          onClose={() => {
            if (isSavingDiagram) {
              return;
            }
            setSavePreview(null);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Confirm diagram changes</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: COLORS.grey[400], mb: 2 }}>
              Review these topology updates before saving.
            </Typography>

            {savePreview && (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {savePreview.summary.addedEdges.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.6 }}>
                      Added dependencies ({savePreview.summary.addedEdges.length})
                    </Typography>
                    <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                      {savePreview.summary.addedEdges.map((edge) => (
                        <li key={edge.id}>
                          <Typography variant="body2" sx={{ color: COLORS.grey[300] }}>
                            {edge.sourceLabel} → {edge.targetLabel}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}

                {savePreview.summary.removedEdges.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.6 }}>
                      Removed dependencies ({savePreview.summary.removedEdges.length})
                    </Typography>
                    <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                      {savePreview.summary.removedEdges.map((edge) => (
                        <li key={edge.id}>
                          <Typography variant="body2" sx={{ color: COLORS.grey[300] }}>
                            {edge.sourceLabel} → {edge.targetLabel}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}

                {savePreview.summary.removedNodes.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.6 }}>
                      Removed nodes ({savePreview.summary.removedNodes.length})
                    </Typography>
                    <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                      {savePreview.summary.removedNodes.map((node) => (
                        <li key={node.id}>
                          <Typography variant="body2" sx={{ color: COLORS.grey[300] }}>
                            {node.label}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="text"
              onClick={() => {
                if (isSavingDiagram) {
                  return;
                }
                setSavePreview(null);
              }}
              disabled={isSavingDiagram}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveRoundedIcon fontSize="small" />}
              onClick={() => {
                void handleConfirmSaveDiagram();
              }}
              disabled={isSavingDiagram}
            >
              {isSavingDiagram ? "Saving..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};
